import {useTranslation} from 'react-i18next'
import React, {useCallback, useEffect, useMemo, useState} from 'react'
import {useFetchData} from '../../hooks/useFetchData'
import {StoredIndividualization} from '../../types/Individualization'
import {useToggleVisibility} from '../../hooks/useToggleVisibility'
import DeleteIndividualizationModal from '../../compositions/Individualization/Overview/DeleteIndividualizationModal'
import IndividualizationList
    from '../../compositions/Individualization/Overview/IndividualizationList/IndividualizationList'
import Heading from '../../components/Heading'
import Pagination from '../../components/Pagination'
import Loader from '../../components/Loader'
import Button from '../../components/Button'
import IconButton from '../../components/IconButton'
import Input from '../../components/Input'
import Select from '../../components/Select'
import {faList, faThLarge} from '@fortawesome/free-solid-svg-icons'
import {useNavigate} from 'react-router-dom'
import {CustomizerContextProvider} from '../../contexts/CustomizerContext'
import IndividualizationGrid
    from '../../compositions/Individualization/Overview/IndividualizationGrid/IndividualizationGrid'
import {ApiRoutes} from '../../services/ApiService'
import {openModal} from '../../services/ModalService'
import {showToast} from '../../services/ToastService'
import CustomizerPopup from '../../compositions/CustomizerPopup/CustomizerPopup'
import {useAdmin} from '../../contexts/AdminContext'
import TrialCard from '../../compositions/Settings/SubscriptionSettings/TrialCard/TrialCard'
import NotificationList from "../../compositions/Notification/NotificationList/NotificationList";
import { CustomerGroup } from '../../types/CustomerGroup'

const SORT_STORAGE_KEY = 'zr_individualization_sort'
const VALID_SORT_VALUES = ['newest', 'oldest', 'name_asc', 'name_desc'] as const

function getStoredSort(): string {
    const stored = localStorage.getItem(SORT_STORAGE_KEY)
    if (stored && VALID_SORT_VALUES.includes(stored as (typeof VALID_SORT_VALUES)[number])) {
        return stored
    }
    return 'newest'
}

const IndividualizationOverview: React.FC = () => {
    const {t} = useTranslation(['individualization'])
    const navigate = useNavigate()
    const {subscription, notifications} = useAdmin()

    const [viewMode, setViewMode] = useState<'card' | 'list'>('card')
    const [searchValue, setSelectedArticle] = useState<string>('')
    const [debounceSearch, setDebounceSearch] = useState<string>('')
    const [sortValue, setSortValue] = useState<string>(getStoredSort)
    const [filterCustomerGroup, setFilterCustomerGroup] = useState<string>('')
    const [currentPage, setCurrentPage] = useState<number>(1)
    const itemsPerPage = useMemo(() => 12, [])

    const sortOptions = useMemo(
        () => [
            {value: 'newest', label: t('overview.sort.newest')},
            {value: 'oldest', label: t('overview.sort.oldest')},
            {value: 'name_asc', label: t('overview.sort.name_asc')},
            {value: 'name_desc', label: t('overview.sort.name_desc')},
        ],
        [t]
    )

    const {
        data: filterCustomerGroupOptions,
        fetch: fetchUsedCustomerGroups,
        isLoading: isLoadingUsedCustomerGroups,
    } = useFetchData<CustomerGroup>(ApiRoutes.CUSTOMER_GROUPS_USED)

    useEffect(() => {
        fetchUsedCustomerGroups()
    }, [fetchUsedCustomerGroups])

    const {
        data: individualizations,
        fetch,
        isLoading,
        pagination,
    } = useFetchData<StoredIndividualization>(ApiRoutes.INDIVIDUALIZATIONS)

    const {toggle} = useToggleVisibility()

    const getIndividualizationById = useCallback(
        (individualization_id: number) => {
            return individualizations.find(
                (data) => data.id === individualization_id
            )
        },
        [individualizations]
    )

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebounceSearch(searchValue)
        }, 750)
        return () => clearTimeout(timer)
    }, [searchValue])

    useEffect(() => {
        fetch({
            page: currentPage.toString(),
            search: debounceSearch,
            perPage: itemsPerPage.toString(),
            sort: sortValue,
            customerGroup: filterCustomerGroup
        })
    }, [fetch, currentPage, debounceSearch, itemsPerPage, sortValue, filterCustomerGroup])

    const deleteIndividualization = (individualization_id: number) => {
        const element = getIndividualizationById(individualization_id)
        if (element) {
            const content: JSX.Element = (
                <DeleteIndividualizationModal
                    individualization={element}
                    onSubmit={() =>
                        fetch({
                            page: currentPage.toString(),
                            search: debounceSearch,
                            perPage: itemsPerPage.toString(),
                            sort: sortValue,
                        })
                    }
                />
            )
            openModal({
                heading: t('overview.delete_modal.heading'),
                content: content,
            })
        }
    }

    const createIndividualization = () => {
        return navigate(`/individualizations/create`)
    }

    const copyIndividualization = (individualization_id: number) => {
        return navigate(`/individualizations/${individualization_id}/copy`)
    }

    const editIndividualization = (individualization_id: number) => {
        return navigate(`/individualizations/${individualization_id}/edit`)
    }

    const toggleVisibility = (individualization_id: number) => {
        const element = getIndividualizationById(individualization_id)
        if (element) {
            toggle(element).then((res) => {
                if (res.status === 200 && res.data?.data) {
                    showToast(t('overview.toggle_visibility.success'))
                } else {
                    showToast(t('overview.toggle_visibility.error'), 'error')
                }
                fetch()
            })
        }
    }

    const demoIndividualization = (individualization_id: number) => {
        const element = getIndividualizationById(individualization_id)
        if (!element) return

        openModal({
            content: (
                <CustomizerContextProvider
                    individualization={element}
                    kArtikel={parseInt(element.kArtikel)}
                >
                    <CustomizerPopup/>
                </CustomizerContextProvider>
            ),
            size: 'lg',
        })
    }

    return (
        <div>
            <TrialCard subscription={subscription}/>
            <NotificationList notifications={notifications}/>
            <Heading level={2} classes={'zr-mt-4'}>
                {t('overview.heading')}
            </Heading>
            <div
                className={
                    'zr-d-flex zr-justify-between zr-items-center zr-flex-wrap zr-mb-4'
                }
                style={{gap: '1rem'}}
            >
                <div className="zr-d-flex zr-gap-3">
                    <Input
                        type="text"
                        placeholder={t('overview.delete_modal.search')}
                        value={searchValue}
                        onChange={(_, value) => {
                            setSelectedArticle(value)
                            setCurrentPage(1)
                        }}
                        property={'search'}
                        classes={'zr-w-100'}
                        style={{minWidth: '300px'}}
                    />
                    {filterCustomerGroupOptions?.length > 0 &&
                        <Select
                            options={
                                filterCustomerGroupOptions.map(
                                    (cg) => ({value: cg.kKundengruppe, label: cg.cName}))
                            }
                            onSelect={(_, value) => {
                                setFilterCustomerGroup(value)
                                setCurrentPage(1)
                            }}
                            property={'sort'}
                            value={filterCustomerGroup}
                            placeholder={t('overview.customer_group_filter')}
                            classes={'zr-w-100'}
                            searchable={true}
                            clearable={true}
                        />
                    }
                    <Select
                        options={sortOptions}
                        onSelect={(_, value) => {
                            setSortValue(value)
                            setCurrentPage(1)
                            localStorage.setItem(SORT_STORAGE_KEY, value)
                        }}
                        property={'sort'}
                        value={sortValue}
                        placeholder={t('overview.sort.placeholder')}
                        classes={'zr-w-100'}
                        searchable={false}
                        clearable={false}
                    />

                </div>

                <div className="zr-d-flex zr-flex-wrap">
                    <Button
                        hierarchy={'primary'}
                        text={t('overview.add')}
                        fn={() => navigate('/individualizations/create')}
                        style={{padding: '0.5em 1.8em', marginRight: '1rem'}}
                    />
                    <IconButton
                        icon={faThLarge}
                        color={viewMode === 'card' ? '#fc0' : '#2f2f2f'}
                        title={t('overview.cardView')}
                        style={{fontSize: '1.25rem'}}
                        onClick={() => setViewMode('card')}
                    />
                    <IconButton
                        icon={faList}
                        color={viewMode === 'list' ? '#fc0' : '#2f2f2f'}
                        title={t('overview.listView')}
                        style={{fontSize: '1.25rem'}}
                        onClick={() => setViewMode('list')}
                    />
                </div>
            </div>

            {isLoading && isLoadingUsedCustomerGroups ? (
                <div className="zr-w-100 zr-d-flex zr-justify-center">
                    <Loader isLoading={isLoading} size="m"/>
                </div>
            ) : viewMode === 'card' ? (
                <IndividualizationGrid
                    individualizations={individualizations}
                    toggleVisibility={toggleVisibility}
                    createIndividualization={createIndividualization}
                    deleteIndividualization={deleteIndividualization}
                    editIndividualization={editIndividualization}
                    copyIndividualization={copyIndividualization}
                    demoIndividualization={demoIndividualization}
                />
            ) : (
                <IndividualizationList
                    individualizations={individualizations}
                    toggleVisibility={toggleVisibility}
                    deleteIndividualization={deleteIndividualization}
                    editIndividualization={editIndividualization}
                    copyIndividualization={copyIndividualization}
                    demoIndividualization={demoIndividualization}
                />
            )}
            <div>
                {pagination && pagination.lastPage > 1 && (
                    <Pagination
                        totalPages={pagination.lastPage}
                        currentPage={currentPage}
                        onPageChange={(p: number) => setCurrentPage(p)}
                        classes={'zr-mt-4'}
                    />
                )}
            </div>
        </div>
    )
}

export default IndividualizationOverview
